import { apiClient } from '@/shared/api/client';
import type { ApiResponse } from '@/shared/types/api.types';
import type {
  CompanyUser,
  CreateCompanyUserRequest,
  UpdateCompanyUserRequest,
} from '../types/company-admin.types';

interface UserResponse {
  id: string;
  email: string;
  profile: {
    firstName: string;
    lastName: string;
    fullName: string;
    phone?: string;
  };
  roles: string[];
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
}

function mapUserResponse(user: UserResponse): CompanyUser {
  return {
    id: user.id,
    email: user.email,
    firstName: user.profile.firstName,
    lastName: user.profile.lastName,
    fullName: user.profile.fullName,
    phone: user.profile.phone,
    roles: user.roles as CompanyUser['roles'],
    isActive: user.isActive,
    lastLoginAt: user.lastLoginAt,
    createdAt: user.createdAt,
  };
}

export const companyUserService = {
  async createUser(request: CreateCompanyUserRequest, companyId: string): Promise<CompanyUser> {
    const response = await apiClient<ApiResponse<{ user: UserResponse }>>('/auth/users', {
      method: 'POST',
      body: {
        email: request.email,
        firstName: request.firstName,
        lastName: request.lastName,
        password: request.password,
        phone: request.phone,
        companyId,
        initialRole: request.initialRole,
        sendPasswordResetEmail: request.sendPasswordResetEmail ?? false,
      },
    });
    return mapUserResponse(response.data.user);
  },

  async updateUser(userId: string, request: UpdateCompanyUserRequest): Promise<CompanyUser> {
    const response = await apiClient<ApiResponse<UserResponse>>(`/auth/users/${userId}`, {
      method: 'PUT',
      body: request,
    });
    return mapUserResponse(response.data);
  },

  async sendPasswordReset(userId: string): Promise<void> {
    await apiClient(`/auth/users/${userId}/password-reset`, { method: 'POST' });
  },

  async deactivateUser(userId: string): Promise<CompanyUser> {
    const response = await apiClient<ApiResponse<UserResponse>>(
      `/auth/users/${userId}/deactivate`,
      {
        method: 'PATCH',
      }
    );
    return mapUserResponse(response.data);
  },

  async activateUser(userId: string): Promise<CompanyUser> {
    const response = await apiClient<ApiResponse<UserResponse>>(`/auth/users/${userId}/activate`, {
      method: 'PATCH',
    });
    return mapUserResponse(response.data);
  },

  async assignRole(userId: string, role: string): Promise<CompanyUser> {
    const response = await apiClient<ApiResponse<UserResponse>>(`/auth/users/${userId}/roles`, {
      method: 'POST',
      body: { role },
    });
    return mapUserResponse(response.data);
  },

  async removeRole(userId: string, role: string): Promise<CompanyUser> {
    const response = await apiClient<ApiResponse<UserResponse>>(
      `/auth/users/${userId}/roles/${role}`,
      {
        method: 'DELETE',
      }
    );
    return mapUserResponse(response.data);
  },
};
